import React, { Component } from "react";
import axios from "axios";
import * as apiMethods from "../res/apiMethods.js";
import FileSaver from "file-saver";
import readXlsxFile from "read-excel-file";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";
import AccessDenied from "./accessDenied.jsx";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

class uvr extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filename: "",
      file: "",
      uvrLoads: [],
      offset: 0,
      count: 0,
      isUploading: false,
      lastUploadDate: null,
      page: 0,
      searchDate: "",
      searchResult: null,
      searchLoading: false,
      searchError: "",
    };
  }

  componentDidMount() {
    document.title = "UVR";
    this.loadInitialData();
  }

  loadInitialData = async () => {
    try {
      const token = window.sessionStorage.getItem("localToken");
      
      // Obtener información inicial del backend
      const response = await axios.get(apiMethods.UVR_INFO, {
        params: { offset: this.state.offset },
        headers: { Authorization: token },
      });

      if (response.data.code === 0) {
        this.setState({
          lastUploadDate: response.data.lastUploadDate,
          uvrLoads: response.data.uvrs,
          count: response.data.count,
        });
      }
    } catch (error) {
      // Solo loguear silenciosamente — no mostrar Swal en errores de red/conexión
      // para evitar alertas agresivas cuando el backend no está disponible
      console.warn("UVR: No se pudo conectar al backend:", error.message);
    }
  };

  handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    this.setState({ file, filename: file.name }, () => {
      this.load();
    });
  };

  download = (e) => {
    e.preventDefault();
    FileSaver.saveAs(
      process.env.PUBLIC_URL + "/EJEMPLO_CARGA_EXCEL.xlsx",
      "EJEMPLO_CARGA_EXCEL.xlsx"
    );
  };

  load = async (e) => {
    if (e) e.preventDefault();
    const { file } = this.state;
    if (!file) return Swal.fire({ icon: "warning", text: "Selecciona un archivo" });

    const token = window.sessionStorage.getItem("localToken");

    this.setState({ isUploading: true });
    Swal.fire({
      title: "Cargando UVR",
      html: `<div>Procesando archivo...</div>`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      // Leer el archivo Excel
      const rows = await readXlsxFile(file);
      
      if (!rows || rows.length === 0) {
        throw new Error("El archivo está vacío.");
      }

      // Validación básica de estructura en frontend
      const badLenIdx = rows.findIndex((r) => !r || r.length < 2);
      if (badLenIdx !== -1) {
        throw new Error(`Fila ${badLenIdx + 1}: se requieren 2 columnas (fecha, valor).`);
      }

      // Preparar datos para enviar al backend
      const uvrs = rows.map((r, i) => {
        const rawDate = r[0];
        const rawVal = r[1];

        // Validar que la fecha sea un objeto Date
        let fecha;
        if (rawDate instanceof Date && !isNaN(rawDate)) {
          fecha = rawDate.toISOString();
        } else {
          throw new Error(
            `Fila ${i + 1}: la fecha debe ser una CELDA de tipo fecha en Excel (formato fecha corta).`
          );
        }

        // Validar el valor
        let valor;
        if (typeof rawVal === "number" && Number.isFinite(rawVal)) {
          valor = rawVal;
        } else if (typeof rawVal === "string") {
          const s = rawVal.trim();
          const hasDotDecimal = /-?\d+\.\d+/.test(s.replace(/\s/g, ""));
          if (hasDotDecimal) {
            throw new Error(
              `Fila ${i + 1}: el valor debe usar COMA como separador decimal (ej: 1.234,56), no punto.`
            );
          }
          const normalized = s.replace(/\./g, "").replace(/,/g, ".");
          valor = parseFloat(normalized);
          if (!Number.isFinite(valor)) {
            throw new Error(`Fila ${i + 1}: valor inválido.`);
          }
        } else {
          throw new Error(`Fila ${i + 1}: valor inválido.`);
        }

        return { fecha, valor };
      });

      // Enviar al backend para validación y carga
      const response = await axios.post(
        apiMethods.UVR_UPLOAD_BATCH,
        { uvrs },
        { headers: { Authorization: token } }
      );

      if (response.data.code === 0) {
        Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: response.data.message,
          html: `
            <p>${response.data.message}</p>
            <p>Creados: ${response.data.results.created}</p>
            <p>Actualizados: ${response.data.results.updated}</p>
            ${response.data.results.errors.length > 0 
              ? `<p style="color: red;">Errores: ${response.data.results.errors.length}</p>` 
              : ''}
          `,
        });
        this.setState({ isUploading: false, file: null, filename: "" });
        this.loadInitialData(); // Recargar datos
      } else {
        throw new Error(response.data.message || "Error en la carga");
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error de validación",
        text: err.response?.data?.message || err.message || "Error durante la carga.",
      });
    } finally {
      this.setState({ isUploading: false });
    }
  };

  searchUVR = async () => {
    const { searchDate } = this.state;
    if (!searchDate) return;
    this.setState({ searchLoading: true, searchResult: null, searchError: "" });
    try {
      const token = window.sessionStorage.getItem("localToken");
      const [yyyy, mm, dd] = searchDate.split("-");
      const formattedDate = `${dd}-${mm}-${yyyy}`;
      const response = await axios.get(apiMethods.GET_UVR_FECHA + formattedDate, {
        headers: { Authorization: token },
      });
      if (response.data?.uvr?.[0]?.valor !== undefined) {
        this.setState({ searchResult: { fecha: response.data.uvr[0].fecha, valor: response.data.uvr[0].valor } });
      } else {
        this.setState({ searchError: "No se encontró UVR para esa fecha." });
      }
    } catch (error) {
      this.setState({ searchError: "No se encontró UVR para la fecha indicada." });
    } finally {
      this.setState({ searchLoading: false });
    }
  };

  render = () => {
    const columns = [
      {
        field: "fecha",
        headerName: "Fecha",
        flex: 1,
        minWidth: 180,
        valueFormatter: (value) => {  
          if (!value) {
            return '-';
          }
          let d = new Date(value); 
          if (isNaN(d.getTime())) {
            return '-';
          }
          const valueFormatted =
            ("0" + d.getUTCDate()).slice(-2) +
            "-" +
            ("0" + (d.getUTCMonth() + 1)).slice(-2) +
            "-" +
            d.getUTCFullYear();
          
          return valueFormatted;
        },
      },
      {
        field: "valor",
        headerName: "Valor",
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <span className="tabular-nums" style={{ fontWeight: 600 }}>
            {params.value}
          </span>
        )
      },
    ];

    const setPage = async (newPage) => {
      const offset = 15 * newPage;
      Swal.fire({ title: "Cargando", allowOutsideClick: false });
      Swal.showLoading();

      try {
        const token = window.sessionStorage.getItem("localToken");
        const response = await axios.get(apiMethods.UVR_INFO, {
          params: { offset },
          headers: { Authorization: token },
        });



        if (response.data.code === 0) {
          this.setState({ offset, uvrLoads: response.data.uvrs, page: newPage, count: response.data.count });
        }
      } catch (error) {
        console.error("Error al cargar página:", error);
      } finally {
        Swal.close();
      }
    };

    const token = window.sessionStorage.getItem("localToken");
    if (!token) {
      return <AccessDenied />;
    }

    let decodedPayload;
    try {
      decodedPayload = JSON.parse(atob(token.split(".")[1]));
    } catch (error) {
      return <AccessDenied />;
    }

    if (decodedPayload.roleId !== 1) {
      return <AccessDenied />;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header de la sección */}
        <div className="section-header">
          <div className="title-group">
            <h1>Tasas de Conversión UVR</h1>
            <p>Carga masiva, consulta histórica y actualización de valores UVR oficiales</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn-mobilia-secondary"
              onClick={this.download}
              disabled={this.state.isUploading}
            >
              <DownloadIcon style={{ fontSize: '1rem' }} /> Descargar Plantilla Excel
            </button>
          </div>
        </div>

        {/* Panel de Carga Masiva */}
        <div className="glass-panel" style={{ padding: '1.75rem' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 1rem 0', color: '#101828' }}>
            Carga Masiva de Archivo UVR
          </h3>

          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            background: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem'
          }}>
            <InfoOutlinedIcon sx={{ color: '#D71920', mt: '2px', fontSize: '1.25rem' }} />
            <div style={{ fontSize: '0.86rem', color: '#475467', lineHeight: 1.55 }}>
              El archivo debe ser un documento <strong>.XLSX</strong> con dos columnas sin encabezados: <strong>Fecha</strong> (tipo fecha corta en orden descendente, de más reciente a más antigua) y <strong>Valor</strong> (numérico, usando coma como separador decimal).
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <input
              type="file"
              id="uvr-file-input"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={this.handleFileSelect}
              disabled={this.state.isUploading}
            />
            <button
              type="button"
              className="btn-mobilia-primary"
              disabled={this.state.isUploading}
              onClick={() => document.getElementById("uvr-file-input")?.click()}
            >
              <UploadFileIcon style={{ fontSize: '1.1rem' }} />
              {this.state.isUploading ? "Procesando archivo…" : "Seleccionar Archivo Excel"}
            </button>

            {this.state.filename && (
              <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#101828' }}>
                Archivo: {this.state.filename}
              </span>
            )}

            {this.state.lastUploadDate && (
              <span className="badge-pill-navy" style={{ marginLeft: 'auto' }}>
                Última fecha cargada: {this.state.lastUploadDate}
              </span>
            )}
          </div>
        </div>

        {/* Panel de Consulta por Fecha */}
        <div className="glass-panel" style={{ padding: '1.5rem 1.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <CalendarTodayIcon sx={{ color: '#D71920', fontSize: '1.2rem' }} />
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: '#101828' }}>
              Consultar UVR por fecha
            </h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <input
              type="date"
              value={this.state.searchDate}
              onChange={(e) => this.setState({ searchDate: e.target.value, searchResult: null, searchError: "" })}
              onKeyDown={(e) => e.key === "Enter" && this.searchUVR()}
              style={{
                border: "1px solid #D0D5DD",
                borderRadius: "8px",
                padding: "0.52rem 0.85rem",
                fontSize: "0.9rem",
                color: '#101828',
                outline: 'none',
                background: '#FFFFFF'
              }}
            />
            <button
              type="button"
              className="btn-mobilia-secondary"
              onClick={this.searchUVR}
              disabled={!this.state.searchDate || this.state.searchLoading}
            >
              <SearchIcon style={{ fontSize: '1rem' }} />
              {this.state.searchLoading ? "Buscando…" : "Buscar"}
            </button>
            {this.state.searchResult && (
              <div className="badge-pill-green" style={{ fontSize: '0.92rem', padding: '0.45rem 1rem' }}>
                {(() => {
                  const d = new Date(this.state.searchResult.fecha);
                  const dateStr = isNaN(d) ? this.state.searchDate
                    : ("0" + d.getUTCDate()).slice(-2) + "-" + ("0" + (d.getUTCMonth() + 1)).slice(-2) + "-" + d.getUTCFullYear();
                  return `UVR al ${dateStr}: ${this.state.searchResult.valor}`;
                })()}
              </div>
            )}
            {this.state.searchError && (
              <span style={{ color: "#D71920", fontSize: '0.88rem', fontWeight: 500 }}>
                {this.state.searchError}
              </span>
            )}
          </div>
        </div>

        {/* Panel Historial UVR */}
        <div className="glass-panel" style={{ padding: '1.5rem' }}>
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: '#101828' }}>
              Historial de Valores UVR
            </h3>
            {this.state.uvrLoads && this.state.uvrLoads.length > 0 && (
              <span style={{ fontSize: '0.82rem', color: '#667085', fontWeight: 500 }}>
                Registros del {this.state.page * 15 + 1} al {this.state.page * 15 + this.state.uvrLoads.length} de {this.state.count}
              </span>
            )}
          </div>
          {this.state.uvrLoads && (
            <div style={{ width: "100%" }}>
              <DataGrid
                rows={this.state.uvrLoads}
                columns={columns}
                rowCount={this.state.count}
                paginationMode="server"
                paginationModel={{ page: this.state.page, pageSize: 15 }}
                onPaginationModelChange={(model) => {
                  if (model.page !== this.state.page) {
                    setPage(model.page);
                  }
                }}
                pageSizeOptions={[15]}
                getRowId={(row) => row._id}
                autoHeight
                sx={{
                  border: '1px solid #EAECF0',
                  borderRadius: '14px',
                  '& .MuiDataGrid-columnHeaders': {
                    background: '#F9FAFB',
                    borderBottom: '1px solid #EAECF0',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: '#667085',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.86rem',
                    color: '#111111',
                    borderBottom: '1px solid #F2F4F7'
                  },
                  '& .MuiDataGrid-row:hover': {
                    background: '#F9FAFB'
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };
}

export default uvr;